import React, { useState } from "react";
import { Modal, TextInput, Textarea, Select, Text, Stack, Group } from "@mantine/core";
import { Button } from "@/components/bnp-ui";
import {
  OTHER_REASON_OPTION,
  PROPERTY_DELETION_QUICK_REASONS,
  deletionReasonHint,
  isDeletionReasonValid,
} from "@/utils/deletionReasons";

interface DeletePropertyModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
  propertyId: string;
  propertyAddress?: string;
  loading?: boolean;
  error?: string | null;
}

const DeletePropertyModal: React.FC<DeletePropertyModalProps> = ({
  opened,
  onClose,
  onConfirm,
  propertyId,
  propertyAddress,
  loading = false,
  error = null,
}) => {
  const [confirmationText, setConfirmationText] = useState("");
  const [reason, setReason] = useState("");

  const isConfirmationValid = confirmationText === "DELETE";
  const isReasonValid = isDeletionReasonValid(reason);
  const canConfirm = isConfirmationValid && isReasonValid;
  const hint = deletionReasonHint(reason);

  const handleQuickReasonChange = (value: string | null) => {
    if (!value) {
      setReason("");
      return;
    }
    setReason(value === OTHER_REASON_OPTION ? "" : value);
  };

  const handleConfirm = async () => {
    if (!canConfirm) {
      return;
    }

    await onConfirm(reason.trim());
    setConfirmationText("");
    setReason("");
  };

  const handleClose = () => {
    setConfirmationText("");
    setReason("");
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Delete Property"
      centered
      size="md"
    >
      <Stack gap="md">
        <div>
          <Text size="sm" fw={500} mb={4}>
            Property ID
          </Text>
          <Text size="sm" c="dimmed">
            {propertyId}
          </Text>
        </div>

        {propertyAddress && (
          <div>
            <Text size="sm" fw={500} mb={4}>
              Address
            </Text>
            <Text size="sm" c="dimmed">
              {propertyAddress}
            </Text>
          </div>
        )}

        <div
          style={{
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "4px",
            padding: "12px",
          }}
        >
          <Text size="sm" fw={600} c="#856404" mb={8}>
            ⚠️ Warning: This action cannot be undone
          </Text>
          <Text size="sm" c="#856404">
            Deleting this property will permanently remove it from the system.
            All associated data will be lost.
          </Text>
        </div>

        <div>
          <Text size="sm" fw={500} mb={8}>
            Select Quick Reason
          </Text>
          <Select
            placeholder="-- Select a reason --"
            value={
              PROPERTY_DELETION_QUICK_REASONS.includes(reason) ? reason : null
            }
            onChange={handleQuickReasonChange}
            data={PROPERTY_DELETION_QUICK_REASONS}
            aria-label="Quick reason"
            disabled={loading}
          />
        </div>

        <div>
          <Text size="sm" fw={500} mb={8}>
            Reason Details <span style={{ color: "#842029" }}>*</span>
          </Text>
          <Textarea
            placeholder="Enter or customize the reason for deleting this property (more than 10 characters)..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            aria-label="Deletion reason"
            aria-required="true"
            rows={3}
            disabled={loading}
          />
          {hint && (
            <Text size="xs" c="dimmed" mt={4}>
              {hint}
            </Text>
          )}
        </div>

        <div>
          <Text size="sm" fw={500} mb={8}>
            Type <strong>DELETE</strong> to confirm
          </Text>
          <TextInput
            placeholder="DELETE"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            aria-label="Confirmation input"
            aria-required="true"
            disabled={loading}
          />
        </div>

        {error && (
          <div
            style={{
              backgroundColor: "#f8d7da",
              border: "1px solid #f5c2c7",
              borderRadius: "4px",
              padding: "12px",
            }}
          >
            <Text size="sm" c="#842029">
              {error}
            </Text>
          </div>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            disabled={!canConfirm || loading}
          >
            {loading ? "Deleting..." : "Confirm Delete"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default DeletePropertyModal;
